// All colours are CSS variables so the whole site re-themes by toggling
// `data-theme` on <html>. The actual dark/light values live in THEME_VARS below.
export const COLORS = {
  bg: 'var(--bg)',
  bg2: 'var(--bg2)',
  surface: 'var(--surface)',
  surfaceHi: 'var(--surface-hi)',
  border: 'var(--border)',
  borderStrong: 'var(--border-strong)',
  borderAccent: 'var(--border-accent)',
  text: 'var(--text)',
  muted: 'var(--muted)',
  faint: 'var(--faint)',
  label: 'var(--label)', // eyebrows/labels — like faint, but a touch darker in light theme

  accent: 'var(--accent)',
  accentBright: 'var(--accent-bright)',
  accentGlow: 'var(--accent-glow)',

  error: 'var(--error)', // form validation / send-failure feedback (desaturated red)
} as const;

// Saira at expanded width (125%) for display headings — a wide, engineered
// grotesque; Inter for body, labels, and the wordmark. Headings must set
// fontStretch: '125%' to select the expanded cut.
export const FONTS = {
  display: "'Saira', sans-serif",
  body: "'Inter', sans-serif",
} as const;

// Shared "utility" treatment for eyebrows, indices, labels, badges, footnotes.
// Echoes the logo wordmark: Inter, uppercase, generously tracked.
export const META: React.CSSProperties = {
  fontFamily: FONTS.body,
  textTransform: 'uppercase',
  letterSpacing: '0.18em',
  fontWeight: 500,
};

// Frosted-glass panel surface, shared across sections (Services, Products,
// About, Contact). The fixed signature shows through, softened. The header
// uses its own heavier glass (--header-glass).
export const GLASS_BG = 'var(--glass)';
export const GLASS_BG_HOVER = 'var(--glass-hover)';

// A frosted-glass surface at an arbitrary background + blur radius. Used for the
// header, mobile menu, and VAE panels (each a different blur); GLASS below is the
// fixed 3px panel glass shared across the page sections.
export const glass = (background: string, blurPx: number): React.CSSProperties => ({
  background,
  backdropFilter: `blur(${blurPx}px)`,
  WebkitBackdropFilter: `blur(${blurPx}px)`,
});
export const GLASS: React.CSSProperties = glass(GLASS_BG, 3);

// The centered content column shared by every section (max 1200px, auto margins).
// Consumers add their own vertical padding + 6vw horizontal gutter.
export const CONTENT_COLUMN: React.CSSProperties = {
  maxWidth: '1200px',
  margin: '0 auto',
};

// Accent-filled primary action (Explore services, Send message, VAE send).
// Squared, accent background, page-bg text. Consumers add padding/typography.
export const ACCENT_BUTTON: React.CSSProperties = {
  background: COLORS.accent,
  color: COLORS.bg,
  border: '1px solid transparent',
  borderRadius: 0,
  cursor: 'pointer',
};

// Cursor-following highlight tints for ReflectionButton, by surface.
export const RIPPLE_ON_DARK = 'var(--ripple-a)';   // buttons over the page surface
export const RIPPLE_ON_LIGHT = 'var(--ripple-b)';  // accent-filled buttons

const THEME_VARS = `
  :root {
    color-scheme: dark;
    --bg: #07070C;
    --bg2: #10101A;
    --surface: #10101A;
    --surface-hi: #17171F;
    --border: rgba(255,255,255,0.10);
    --border-strong: rgba(255,255,255,0.20);
    --border-accent: rgba(201,204,214,0.40);
    --text: #F4F4F8;
    --muted: rgba(244,244,248,0.56);
    --faint: rgba(244,244,248,0.34);
    --label: rgba(244,244,248,0.34);
    --nav-hover: rgba(244,244,248,0.75);
    --accent: #C9CCD6;
    --sig-color: #C9CCD6;
    --accent-bright: #FFFFFF;
    --accent-glow: rgba(201,204,214,0.10);
    --glass: rgba(17,17,27,0.25);
    --glass-hover: rgba(28,28,40,0.35);
    --ripple-a: rgba(255,255,255,0.16);
    --ripple-b: rgba(7,7,12,0.16);
    --header-glass: rgba(17,17,27,0.82);
    --mobile-glass: rgba(7,7,12,0.97);
    --vae-glass: rgba(7,7,12,0.75);
    --shimmer-base: #F4F4F8;
    --shimmer-band: #54545C;
    --sig-flash: #FFFFFF;
    --error: #E08A83;
  }
  :root[data-theme='light'] {
    color-scheme: light;
    --bg: #F4F4F6;
    --bg2: #FFFFFF;
    --surface: #F5F2ED;
    --surface-hi: #EEEBE4;
    --border: rgba(0,0,0,0.12);
    --border-strong: rgba(0,0,0,0.22);
    --border-accent: rgba(90,90,100,0.40);
    --text: #16161A;
    --muted: rgba(22,22,26,0.60);
    --faint: rgba(22,22,26,0.40);
    --label: rgba(22,22,26,0.58);
    --nav-hover: rgba(22,22,26,0.75);
    --accent: #565660;
    --sig-color: #43434C;
    --accent-bright: #16161A;
    --accent-glow: rgba(90,90,100,0.10);
    --glass: rgba(245,242,237,0.45);
    --glass-hover: rgba(238,233,222,0.68);
    --ripple-a: rgba(0,0,0,0.12);
    --ripple-b: rgba(255,255,255,0.45);
    --header-glass: rgba(245,242,237,0.85);
    --mobile-glass: rgba(246,243,239,0.97);
    --vae-glass: rgba(226,222,214,0.75);
    --shimmer-base: #16161A;
    --shimmer-band: #B4B4BC;
    --sig-flash: #16161A;
    --error: #B23A31;
  }
`;

export const injectGlobalCSS = (): void => {
  const id = 'novaeon-global';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent =
    THEME_VARS +
    `
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    /* Header inset — shared by the real header AND the intro's docked logo so
       they land on exactly the same spot. Overridden per-breakpoint below. */
    html { scroll-behavior: smooth; overflow-x: hidden; --header-pad-x: 6vw; --header-pad-y: 1.1rem; }
    body {
      background: ${COLORS.bg};
      color: ${COLORS.text};
      font-family: ${FONTS.body};
      font-size: 16px;
      line-height: 1.7;
      overflow-x: hidden;
      -webkit-font-smoothing: antialiased;
      text-rendering: optimizeLegibility;
    }
    ::selection { background: ${COLORS.accent}; color: ${COLORS.bg}; }
    :focus-visible {
      outline: 1px solid ${COLORS.accentBright};
      outline-offset: 2px;
    }
    input:focus, textarea:focus {
      border-color: ${COLORS.accent} !important;
      outline: none;
    }
    input::placeholder, textarea::placeholder { color: ${COLORS.faint}; }
    /* iOS auto-zooms into a focused field whose font-size is < 16px. Bump text
       inputs to 16px on touch devices to stop that zoom (negligible visual change,
       and avoids disabling pinch-zoom which would hurt accessibility). !important
       beats the inline styles on the VAE + contact inputs. VAE message text is
       bumped to match so the conversation (input + messages) stays consistent. */
    @media (pointer: coarse) {
      input, textarea, select { font-size: 16px !important; }
      .vae-body { font-size: 16px !important; }
    }
    /* On narrow portrait phones, stretch the VAE chat panel to fill the width with
       symmetric margins that MATCH the page content gutters (.section-wrap uses 1.5rem
       side padding on mobile). Its default is right-anchored (right: 1.5rem) at 100vw-2rem
       wide, which leaves an uneven sliver on the left; this balances it to the page edges. */
    @media (max-width: 500px) {
      .vae-panel {
        left: 1.5rem !important;
        right: 1.5rem !important;
        width: auto !important;
      }
    }
    /* Hide the floating reCAPTCHA v3 badge (it clashes with the minimal design and
       collides with the VAE launcher). Google's terms allow hiding it PROVIDED the
       disclosure text is shown in the flow — we put that in the footer. Use
       visibility, not display:none, so token generation keeps working. */
    .grecaptcha-badge { visibility: hidden; }
    @keyframes shimmer {
      0% { background-position: 150% center; }
      100% { background-position: -150% center; }
    }
    .services-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .services-grid > * {
      border-right: 1px solid ${COLORS.border};
      border-bottom: 1px solid ${COLORS.border};
    }
    .services-grid > *:nth-child(2n) { border-right: none; }
    .services-grid > *:nth-last-child(-n+2) { border-bottom: none; }
    .menu-toggle { display: none; }
    /* Collapse the header nav into the hamburger before the full desktop header
       runs out of room (nav would otherwise collide with the logo and push the
       theme toggle off-screen). The toggle lives outside the nav, so it stays
       visible in this collapsed state. */
    @media (max-width: 900px) {
      .nav-links { display: none !important; }
      .header-cta { display: none !important; }
      .menu-toggle { display: inline-flex !important; }
    }
    @media (max-width: 860px) {
      /* Keep the ambient signature on mobile, centered behind the content and
         sized off the viewport so it scales with the screen. Knob: 66vw. */
      .hero-signature {
        width: 66vw !important;
        right: 50% !important;
        transform: translate(50%, -50%) !important;
      }
    }
    @media (max-width: 700px) {
      html { --header-pad-x: 5vw; --header-pad-y: 1rem; }
      .section-wrap { padding-left: 1.5rem !important; padding-right: 1.5rem !important; padding-top: 4.5rem !important; padding-bottom: 4.5rem !important; }
      .services-grid { grid-template-columns: 1fr; }
      .services-grid > * { border-right: none !important; border-bottom: 1px solid ${COLORS.border} !important; }
      .services-grid > *:last-child { border-bottom: none !important; }
      /* Two-column layouts stack on mobile so nothing forces the page wider than the viewport. */
      .contact-inner { grid-template-columns: 1fr !important; gap: 2.75rem !important; }
      .contact-formrow { grid-template-columns: 1fr !important; }
      .about-grid { grid-template-columns: 1fr !important; }
      /* On mobile, lead with the body copy and drop the founder photo beneath it. */
      .about-grid > :first-child { order: 2; width: 100% !important; }
      .about-grid > :last-child { order: 1; }
      /* Stack the hero capability items vertically on mobile. */
      .spec-strip { flex-direction: column; gap: 1rem !important; }
      /* Document pages take the same side gutters as .section-wrap on mobile, but keep their
         own vertical padding — a document opens near the top, not 4.5rem down. */
      .document-wrap { padding-left: 1.5rem !important; padding-right: 1.5rem !important; }
      /* Expanded SandwichContainer content is indented to align under the row title on
         desktop. On a phone that indent costs a quarter of the reading width, which matters
         most for the long documents on /legal. */
      .sandwich-inner { padding-left: 0 !important; }
    }
    @media (prefers-reduced-motion: reduce) {
      html { scroll-behavior: auto; }
      .shimmer-word { animation: none !important; }
      * { transition: none !important; }
    }
  `;
  document.head.appendChild(style);
};
